import * as React from 'react';
import Select, { SelectValue } from '../../../../Components/Base/Select/Select';
import {
	useRootWorkplaceByChildQuery,
	useWorkplacesForSelectQuery,
} from '../../../../graphql/graphql';
import { FieldError } from 'react-hook-form';
import { useEffect, useMemo } from 'react';
import Loading from '../../../../Components/Loading';
import { useAppContext } from '../../../../Services/AppContext';
import useTranslation from '../../../../Hooks/useTranslation';

interface Props {
	onChange?: (value: SelectValue) => void;
	value?: SelectValue;
	error?: FieldError;
	portal?: boolean;
	workplaceType?: string;
	workplaceTypes?: string[];
	filterByRootWorkplaceId?: boolean;
	excludedIds?: string[];
	disabled?: boolean;
	managed?: boolean;
}

const WorkplaceSelect: React.FC<Props> = props => {
	const {
		value,
		onChange,
		portal = false,
		workplaceType = 'normal',
		workplaceTypes,
		filterByRootWorkplaceId,
		excludedIds,
		disabled,
		managed,
	} = props;
	const { t } = useTranslation();

	const queryArgs: any = {
		take: 999,
		type: workplaceTypes ? undefined : workplaceType,
		managed: managed,
	};
	if (filterByRootWorkplaceId) {
		const rootWorkplace = useRootWorkplaceByChildQuery({
			workplaceId: useAppContext().workplaceId,
		});
		queryArgs.rootWorkplaceId =
			rootWorkplace?.data?.workplace?.rootWorkplace?.id;
	}
	const { data, isLoading } = useWorkplacesForSelectQuery(queryArgs);

	const options: SelectValue[] = useMemo(() => {
		const items: SelectValue[] = [];

		for (const workplace of data?.workplaces?.data || []) {
			if (workplace && !excludedIds?.some(item => item === workplace.id)) {
				if (
					workplaceTypes &&
					workplaceTypes?.length > 0 &&
					workplace.type &&
					!workplaceTypes.includes(workplace.type)
				) {
					continue;
				}
				items.push({
					value: workplace.id,
					label: workplace.doctorDisplayName || '',
				});
			}
		}

		return items;
	}, [data, excludedIds, workplaceTypes]);

	const val = useMemo(() => {
		if (value?.value) {
			return value;
		}
		const newVal = options?.find(item => item.value === value);
		if (newVal) {
			onChange?.(newVal);
			return newVal;
		}
		return undefined;
	}, [value, options]);

	if (isLoading) {
		return <Loading />;
	}

	return (
		<Select
			options={options}
			{...props}
			onChange={onChange}
			value={val}
			portal={portal}
			placeholder={t('base.shared.pick')}
			disabled={disabled}
			showErrorMessage={false}
		/>
	);
};

export default WorkplaceSelect;
